<template>
  <TravelPlannerHero
    :showChangeButton="true"
    :label="heroLabel"
  />
  <div class="grid-layout page-wrapper">
    <section class="grid-layout__main">
      <CalendarSearch
        :show-outbound="showOutbound"
      />
      <div
        v-if="
          !hasResults || (!showOutbound && hasReturnTravel && !hasReturnResults)
        "
        class="search-result__no-results"
      >
        {{ $t('searchResult.noResults') }}
      </div>
      <JourneySearchResult
        v-else
        @update-selected-travel="updateSelectedTravel"
        @update-selected-return-travel="updateSelectedReturnTravel"
        @update-show-outbound="updateShowOutbound"
        :show-outbound="showOutbound"
        :is-rebooking="false"
      />
    </section>
    <section
      class="grid-layout__sidebar"
      v-if="!isMobileView"
    >
      <MyTravel
        :origin="origin"
        :destination="destination"
        :has-return-travel="hasReturnTravel"
        :outbound-date="outboundDate"
        :return-date="returnDate"
        :outbound-time="outboundTime"
        :return-time="returnTime"
        :passengers="passengers"
        :showTotalPrice="false"
      />
    </section>
  </div>
  <NavigationBar :isColumn="true">
    <div class="navigation-bar__upper">
      <PriceTag :is-rebooking="false" />
      <button
        class="navigation-bar__details-button button button--reset"
        @click="toggleDrawer"
        v-if="isMobileView"
      >
        {{ $t('searchResult.navigation.showDetails') }}
        <svg class="trailing__chevron">
          <use :xlink:href="`#${chevron}`" />
        </svg>
      </button>
    </div>
    <div class="navigation-bar__lower">
      <router-link
        class="button button--medium button--secondary"
        :to="backRoute"
      >
        {{ $t('searchResult.navigation.back') }}
      </router-link>
      <BookingHandler
        class="search-result-page__booking-handler"
        :selectedTravel="selectedTravelObject"
        :selectedReturnTravel="selectedReturnTravelObject"
        :returnTravels="returnTravels"
        :showInboundTravels="showInboundTravels"
      />
    </div>
  </NavigationBar>
  <BottomDrawer
    v-if="isMobileView"
    :is-open="isDrawerOpen"
    @close="toggleDrawer"
  >
    <template #header>
      <h2 class="title-medium">
        {{ $t('searchResult.navigation.drawerTitel') }}
      </h2>
    </template>
    <MyTravel
      :origin="origin"
      :destination="destination"
      :has-return-travel="hasReturnTravel"
      :outbound-date="outboundDate"
      :return-date="returnDate"
      :outbound-time="outboundTime"
      :return-time="returnTime"
      :passengers="passengers"
      :showTotalPrice="false"
    />
    <template #footer>
      <button
        class="button button--medium button--secondary button--fullwidth"
        @click="toggleDrawer"
      >
        Lukk
      </button>
    </template>
  </BottomDrawer>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useJourneyStore } from '@/stores/journeyStore'
import { useBookingStore } from '@/stores/bookingStore'
import { useSeatReservationStore } from '@/stores/seatReservationStore'
import { useMyTravelStore } from '@/stores/myTravelStore'
import { useErrorStore } from '@/stores/errorStore'
import { useUserStore } from '@/stores/userStore'
import { getStoredProvisionalBookingNumber, clearProvisionalBookingInfo } from '@/services/storageService'
import { cancelProvisionalBooking, performJourneySearch, fetchAllStations } from '@/services/journeyService'
import { useSearchResultSelection } from '@/composables/useSearchResultSelection'
import { useSearchResultDisplay } from '@/composables/useSearchResultDisplay'
import { isValid } from 'date-fns'
import chevron from '@/assets/icons/chevron.svg'

// Components
import JourneySearchResult from '@/components/TravelPlanner/JourneySearchResult/JourneySearchResult.vue'
import MyTravel from '@/components/TravelPlanner/MyTravel/MyTravel.vue'
import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue'
import TravelPlannerHero from '@/components/TravelPlanner/TravelPlannerHero.vue'
import BookingHandler from '@/components/TravelPlanner/BookingHandler.vue'
import PriceTag from '@/components/TravelPlanner/PriceTag.vue'
import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'
import CalendarSearch from '@/components/TravelPlanner/CalendarSearch.vue'

export default defineComponent({
  name: 'SearchResultPage',
  components: {
    TravelPlannerHero,
    JourneySearchResult,
    MyTravel,
    NavigationBar,
    BookingHandler,
    PriceTag,
    BottomDrawer,
    CalendarSearch,
  },
  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const journeyStore = useJourneyStore()
    const bookingStore = useBookingStore()
    const errorStore = useErrorStore()
    const seatReservationStore = useSeatReservationStore()
    const myTravelStore = useMyTravelStore()
    
    // Always in booking mode for this page
    const isRebooking = computed(() => false)
    
    // Use our composables
    const {
      selectedTravel,
      selectedReturnTravel,
      showOutbound,
      updateSelectedTravel,
      updateSelectedReturnTravel,
      updateShowOutbound,
      selectedTravelObject,
      selectedReturnTravelObject,
      returnTravels,
      hasResults,
      hasReturnResults,
      showInboundTravels
    } = useSearchResultSelection()
    
    const {
      heroLabel,
      origin,
      destination,
      hasReturnTravel,
      passengers,
      outboundTime,
      returnTime,
      outboundDate,
      returnDate,
      isDrawerOpen,
      isMobileView,
      toggleDrawer,
      checkMobileView,
      backRoute,
      getQueryString
    } = useSearchResultDisplay(isRebooking)

    // Helper function to validate date string format
    const isValidDateString = (dateStr: string): boolean => {
      const regex = /^\d{4}-\d{2}-\d{2}$/
      if (!regex.test(dateStr)) return false
      const parsed = new Date(dateStr)
      return isValid(parsed)
    }

    onMounted(async () => {
      const outboundDate = getQueryString(route.query.outboundDate)
      const departureDate = getQueryString(route.query.departureDate)
      const returnDate = getQueryString(route.query.returnDate)
      const passengers = getQueryString(route.query.passengers)
      const origin = getQueryString(route.query.origin)
      const destination = getQueryString(route.query.destination)

      if (!journeyStore.allStations.length) {
        try {
          const stations = await fetchAllStations()
          journeyStore.setAllStations(stations)
        } catch (error) {
          console.error('Failed to fetch stations:', error)
          errorStore.setError('Failed to load stations')
          return
        }
      }

      let originStation = null
      let destinationStation = null

      if (origin) {
        originStation =
          journeyStore.allStations.find(
            (s) => s.name === origin || s._u_i_c_station_code === origin
          ) ||
          journeyStore.allStations.find((s) =>
            s.name.toLowerCase().includes(origin.toLowerCase())
          )
        if (originStation) {
          journeyStore.setOriginStation(originStation)
        } else {
          console.warn(
            `Origin station with name or UIC code ${origin} not found`
          )
          errorStore.setError(`Invalid origin station: ${origin}`)
        }
      }

      if (destination) {
        destinationStation =
          journeyStore.allStations.find(
            (s) =>
              s.name === destination || s._u_i_c_station_code === destination
          ) ||
          journeyStore.allStations.find((s) =>
            s.name.toLowerCase().includes(destination.toLowerCase())
          )
        if (destinationStation) {
          journeyStore.setDestinationStation(destinationStation)
        } else {
          console.warn(
            `Destination station with name or UIC code ${destination} not found`
          )
          errorStore.setError(`Invalid destination station: ${destination}`)
        }
      }

      const date = outboundDate || departureDate

      if (date) {
        if (isValidDateString(date)) {
          journeyStore.setDepartureDate(date)
        } else {
          console.warn('Invalid departure date format:', date)
          errorStore.setError('Invalid departure date format')
        }
      }

      if (returnDate && returnDate !== '') {
        if (isValidDateString(returnDate)) {
          journeyStore.setReturnDate(returnDate)
        } else {
          console.warn('Invalid return date format:', returnDate)
          errorStore.setError('Invalid return date format')
        }
      } else {
        journeyStore.setReturnDate(null)
      }

      let formattedPassengers: any[] = []
      if (passengers) {
        try {
          const parsedPassengers = JSON.parse(passengers)
          if (Array.isArray(parsedPassengers)) {
            formattedPassengers = parsedPassengers.map((p, index) => ({
              id:
                p.id ||
                `passenger_${Date.now()}_${Math.random()
                  .toString(36)
                  .slice(2)}`,
              type: p.type,
              discount_cards: p.discount_cards || [],
            }))
            journeyStore.setPassengers(formattedPassengers)
          }
        } catch (error) {
          console.warn(
            'Failed to parse passengers from query:',
            passengers,
            error
          )
          errorStore.setError('Invalid passenger data')
        }
      }

      if (!journeyStore.journeySearchResults?.data?.offer?.travels?.length) {
        if (
          originStation &&
          destinationStation &&
          journeyStore.departureDate &&
          formattedPassengers.length
        ) {
          try {
            await performJourneySearch({
              origin: originStation,
              destination: destinationStation,
              outboundDate: journeyStore.departureDate,
              returnDate: journeyStore.returnDate,
              passengers: formattedPassengers,
            })
          } catch (error) {
            console.error('Failed to perform journey search:', error)
            errorStore.setError('Failed to load search results')
          }
        } else {
          console.warn(
            'Cannot perform search, missing required parameters:',
            {
              origin: originStation,
              destination: destinationStation,
              outboundDate: journeyStore.departureDate,
              passengers: formattedPassengers,
            }
          )
        }
      }

      checkMobileView()
      window.addEventListener('resize', checkMobileView)

      try {
        const storedBookingNumber = getStoredProvisionalBookingNumber();
        
        if (storedBookingNumber && (!bookingStore.bookingData || bookingStore.bookingNumber !== storedBookingNumber)) {
          try {
            bookingStore.setBookingNumber(storedBookingNumber);
            await bookingStore.fetchBookingData(storedBookingNumber);
          } catch (fetchError) {
            console.error('Failed to fetch booking data for stored booking:', fetchError);
            clearProvisionalBookingInfo();
          }
        }
        
        if (bookingStore.bookingData) {
          if (!bookingStore.bookingData.confirmed_timestamp) {
            try {
              await cancelProvisionalBooking(bookingStore.bookingNumber as string, true);
            } catch (cancelError) {
              console.log('Silent error cancelling booking:', cancelError);
            }
          }
        }
        
        if (storedBookingNumber) {
          clearProvisionalBookingInfo();
        }

        bookingStore.$reset();
        seatReservationStore.$reset();
        myTravelStore.reset();
      } catch (error) {
        console.log('Error in SearchResultPage setup:', error);
        try {
          clearProvisionalBookingInfo();
        } catch {}
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobileView)
    })

    return {
      origin,
      destination,
      hasReturnTravel,
      outboundDate,
      returnDate,
      outboundTime,
      returnTime,
      passengers,
      selectedTravel,
      selectedReturnTravel,
      returnTravels,
      showInboundTravels,
      heroLabel,
      showOutbound,
      updateSelectedTravel,
      updateSelectedReturnTravel,
      updateShowOutbound,
      bookingStore,
      seatReservationStore,
      isMobileView,
      isDrawerOpen,
      toggleDrawer,
      chevron,
      myTravelStore,
      hasResults,
      hasReturnResults,
      selectedTravelObject,
      selectedReturnTravelObject,
      backRoute,
    }
  },
})
</script>

<style lang="scss" scoped>
.search-result {
  &__no-results {
    text-align: center;
    padding: 2rem;
    margin: 1rem 0;
    font-size: 1rem;
  }
}

.navigation-bar {
  &__lower {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 8px;
  }

  @media screen and (max-width: $breakpoint-sm) {
    &__upper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 8px;
    }
  }
}

.search-result-page {
  &__booking-handler {
    @media screen and (max-width: 400px) {
      display: none;
    }
  }
}
</style>
